import CalendarIcon from '@mui/icons-material/CalendarMonth';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {useQueryClient} from '@tanstack/react-query';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {array, bool, func, object} from 'prop-types';
import React, {useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import AreaIndicator from '../common/AreaIndicator.jsx';
import FeaturesGrid from './FeaturesGrid.jsx';
import ProgressBackdrop from './ProgressBackdrop.jsx';
import QuotaDisplay from './QuotaDisplay.jsx';
import ReleaseConfirmationModal from './ReleaseConfirmationModal.jsx';
import {notify} from '@actions/notifications';
import {queryKeys as productsQueryKeys} from '@api/products';
import {
  queryKeys as reservationsQueryKeys,
  useDeleteQuotaReservation,
} from '@api/quotaReservations';
import {trackEvent} from '@app/analytics';
import {formatProductTermRange} from '@app/util/format-dates';
import {getHashFromId, refEquals} from '@app/util/granero';

const ReservationCard = ({
  product,
  reservations,
  selectedFeatureRefs,
  setSelectedFeatureRefs,
  onZoomToFeature,
  features,
  loadingFeatures,
  collection,
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const {allowReleaseQuota} = useFlags() ?? {};

  const [featuresExpanded, setFeaturesExpanded] = useState(false);
  const [isReleasing, setIsReleasing] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const {mutateAsync: deleteReservation} = useDeleteQuotaReservation();

  const costsById = useMemo(() => {
    const costs = {};
    reservations?.forEach(reservation => {
      costs[reservation.aoi_ref] = {
        cost: reservation.amount,
      };
    });
    return costs;
  }, [reservations]);

  const selectionsCost = useMemo(() => {
    let cost = 0;
    selectedFeatureRefs?.forEach(featureRef => {
      cost += costsById[featureRef]?.cost || 0;
    });

    return cost;
  }, [costsById, selectedFeatureRefs]);

  const reservedFeatures = useMemo(
    () =>
      features.filter(f =>
        f.relations?.find(
          relation =>
            relation.system === 'admin' &&
            relation.key === 'pa' &&
            relation.value === product.id.toString()
        )
      ),
    [features, product.id]
  );

  const selectedFeatureRefHashes = (selectedFeatureRefs ?? []).map(r =>
    getHashFromId(r)
  );

  // If a collection was renamed, the aoi_refs for its features will change, so
  // we need to make sure the selected row list is using current aoi_refs
  const selectedRows = (selectedFeatureRefs ?? [])
    .map(selectedRow => {
      const feature = features.find(f =>
        refEquals(f.properties['pl:ref'], selectedRow)
      );
      return feature?.properties['pl:ref'];
    })
    .filter(row => !!row);

  const onRowClick = newModel => {
    const allIds = [...newModel.ids];
    setSelectedFeatureRefs(allIds);
  };

  const handleClose = () => {
    setOpenConfirmation(false);
    trackEvent('Reservation Card', {
      action: 'closed release confirmation modal',
    });
  };

  const onReleaseClick = async () => {
    setIsReleasing(true);

    const toRelease = reservations.filter(r =>
      selectedFeatureRefHashes.includes(getHashFromId(r.aoi_ref))
    );

    const promises = toRelease.map(reservation =>
      deleteReservation({id: reservation.id, collectionId: collection.id})
    );

    await Promise.allSettled(promises).then(resp => {
      const successResp = resp
        .filter(d => d.status === 'fulfilled')
        .map(d => d.value);

      const errorsResp = resp.filter(d => d.status === 'rejected');

      setIsReleasing(false);

      queryClient.invalidateQueries({queryKey: productsQueryKeys.lists()});

      if (collection) {
        queryClient.invalidateQueries({
          queryKey: reservationsQueryKeys.list({
            collection_id: collection.id,
          }),
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: reservationsQueryKeys.lists(),
        });
      }

      if (successResp.length > 0) {
        dispatch(
          notify({
            message: `Successfully released quota for ${product.title}`,
            severity: 'success',
            persist: true,
          })
        );

        trackEvent('Reservation Card', {
          action: 'released quota successfully',
          productTitle: product.title,
          totalReservations: successResp.length,
        });
      }

      if (errorsResp.length > 0) {
        dispatch(
          notify({
            message: `There was an error while releasing ${successResp.length > 0 ? 'some ' : ''}reservations for ${product.title}`,
            severity: 'error',
            persist: true,
          })
        );
      }

      setOpenConfirmation(false);
    });
  };

  return (
    <>
      <Card
        elevation={18}
        sx={{boxShadow: 'none', border: '1px solid', borderColor: 'divider'}}
      >
        <CardContent>
          <Grid container rowSpacing={1} direction="column">
            <Stack
              direction="row"
              sx={{
                width: '100%',
                marginBottom: 1,
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  display: 'inline',
                }}
              >
                {product.title}

                <Tooltip
                  title={`Access dates: ${formatProductTermRange(product)}`}
                  placement="top"
                >
                  <CalendarIcon
                    color="action"
                    sx={{
                      marginTop: -0.5,
                      verticalAlign: 'middle',
                      marginLeft: 1,
                    }}
                  />
                </Tooltip>
              </Typography>

              {allowReleaseQuota && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  disabled={isReleasing}
                  onClick={() => setOpenConfirmation(true)}
                  sx={{marginLeft: 'auto', flexShrink: 0}}
                >
                  {isReleasing ? 'Releasing...' : 'Release quota'}
                </Button>
              )}
            </Stack>

            <Grid
              container
              direction="row"
              size={12}
              sx={{
                alignItems: 'center',
              }}
            >
              <Grid size={8}>
                <Typography>Current balance</Typography>
              </Grid>

              <Grid size={4}>
                <Typography
                  variant="subtitle1"
                  color="primary"
                  sx={{
                    textAlign: 'right',
                  }}
                >
                  <QuotaDisplay
                    quota={product.quota_total - product.quota_used}
                    isUnlimited={product.unlimited_quota}
                  />
                </Typography>
              </Grid>
            </Grid>

            <Grid
              container
              direction="row"
              size={12}
              sx={{
                alignItems: 'center',
              }}
            >
              <Grid size={8}>
                <Typography>To release</Typography>
              </Grid>

              <Grid size={4}>
                <Typography
                  sx={{
                    textAlign: 'right',
                  }}
                >
                  <AreaIndicator squareMeters={selectionsCost} />
                </Typography>
              </Grid>
            </Grid>

            {!product.unlimited_quota && (
              <>
                <Grid container direction="row" sx={{marginY: 1}} size={12}>
                  <Grid size={9}></Grid>

                  <Grid size={3}>
                    <Divider flexItem />
                  </Grid>
                </Grid>

                <Grid
                  container
                  direction="row"
                  size={12}
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Grid size={8}>
                    <Typography>New balance</Typography>
                  </Grid>

                  <Grid size={4}>
                    <Typography
                      sx={{
                        textAlign: 'right',
                      }}
                    >
                      <AreaIndicator
                        squareMeters={
                          product.quota_total -
                          product.quota_used +
                          selectionsCost
                        }
                      />
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>

        <Divider sx={{marginTop: 1}} />

        <CardActions>
          <IconButton
            size="small"
            disabled={loadingFeatures}
            onClick={() => setFeaturesExpanded(prevState => !prevState)}
            data-testid="features-table-expand-button"
            sx={{
              transform: !featuresExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: theme =>
                theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shortest,
                }),
            }}
          >
            {loadingFeatures ? (
              <CircularProgress size={24} />
            ) : (
              <ExpandMoreIcon />
            )}
          </IconButton>

          <Typography variant="subtitle1">
            Features {loadingFeatures ? '' : `(${reservedFeatures.length})`}
          </Typography>
        </CardActions>

        <Collapse
          in={featuresExpanded}
          timeout="auto"
          mountOnEnter
          unmountOnExit
          sx={{
            '.MuiCollapse-wrapper': {
              // keeps data grid happy and not janky
              height: '100%',
              minHeight: 250,
              maxHeight: 500,
            },
          }}
        >
          <FeaturesGrid
            features={reservedFeatures}
            estimates={costsById}
            rowSelectionModel={{
              type: 'include',
              ids: new Set(selectedRows?.length ? selectedRows : null),
            }}
            onRowSelectionModelChange={onRowClick}
            loading={loadingFeatures}
            disableSelection={isReleasing}
            onZoomToClicked={onZoomToFeature}
            hideSelection={!allowReleaseQuota}
            collection={collection}
          />
        </Collapse>
      </Card>

      <ProgressBackdrop open={isReleasing} title="Releasing quota" />

      <ReleaseConfirmationModal
        open={openConfirmation}
        onReleaseClick={onReleaseClick}
        isReleasing={isReleasing}
        onClose={handleClose}
      />
    </>
  );
};

ReservationCard.propTypes = {
  features: array,
  product: object,
  loadingFeatures: bool,
  reservations: array,
  selectedFeatureRefs: array,
  setSelectedFeatureRefs: func,
  onZoomToFeature: func,
  collection: object.isRequired,
};

export default ReservationCard;
